import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import {
  E_KHATTABAT_CONVERSATION_TYPE,
  Gender,
  TalkhattabatConversationType,
  TGender,
  TKhattabatMessage,
  TMessagesType,
  TSelectionPages
} from "../../../Types";
import React from "react";
import { isDefinedString, isPositiveNumber } from "et3-type-guards";
import { E_CHAT_SENDER } from "et3-ui-library/mui";
import { Ti18nKeys } from "../../../constants/i18n";
import { MAX_APP_MESSAGE_LENGTH } from "../../../constants/input-entries-constants";
import getKhattabat101MMessagingStore from "../101m-stores/101m-messaging-store";
import getKhattabat101CMessagingStore from "../101c-stores/101c-messaging-store";
import getKhattabatFilterStore from "../filter-store";

import getKhattabat101MessagingStore from "./101-messaging-store";
import { getFormmatedDate } from "../../../helpers/et3-date-helper";
import getKhattabat102MessagingStore from "./102-messaging-store";
import { getKhattabat101CGeneralStore } from "../101c-stores/101c-general-store";
class KhattabatSharedMessagingStore {
  conversationID = observable.box<string>("");
  conversationType = observable.box<TalkhattabatConversationType>("101C");
  messages = observable.box<TKhattabatMessage[]>([]);
  unreadMessagesCounter = observable.box<number>(0);
  messageField = observable.box<string>("");
  isOpened = observable.box<"OPENED" | "CLOSED">("CLOSED");
  isLoading = observable.box<boolean>(false);
  isSending = observable.box<boolean>(false);
  scrollRef = React.createRef<HTMLDivElement>();
  isSeen = observable.box<1 | 0>(0);
  hasUnreadMessages = observable.box<boolean>(false);

  setMessageField = (value: string) => {
    if (value.length > MAX_APP_MESSAGE_LENGTH) {
      return;
    }
    const isFirstCharacter = !isDefinedString(this.messageField.get());
    const finalizedValue = isFirstCharacter ? value.trim() : value;
    runInAction(() => {
      this.messageField.set(finalizedValue);
    });
  };

  toggleIsOpened = () => {
    const currentState = this.isOpened.get();
    runInAction(() => {
      this.isOpened.set(currentState === "OPENED" ? "CLOSED" : "OPENED");
    });
  };
  setUnreadMessagesCounter = (value: number) => {
    runInAction(() => {
      this.unreadMessagesCounter.set(value);
      this.isSeen.set(value > 0 ? 0 : 1);
      this.hasUnreadMessages.set(value > 0);
    });
  };

  appendMessage = (message: TKhattabatMessage) => {
    runInAction(() => {
      this.messages.set([...this.messages.get(), message]);
    });
    this.scrollToBottom();
  };
  updateMessages = (messages: TKhattabatMessage[]) => {
    runInAction(() => {
      this.messages.set(messages);
    });
  };
  markSentMessagesAsSeen = () => {
    const seenMessages: TKhattabatMessage[] = this.messages
      .get()
      .map((khattabatMessage) =>
        isPositiveNumber(khattabatMessage.isKhattabaMessage)
          ? { ...khattabatMessage, isSeen: 1 }
          : khattabatMessage
      );

    this.updateMessages(seenMessages);
  };
  resetMessages = () => {
    runInAction(() => {
      this.messages.set([]);
    });
  };

  sendMessage = () => {
    switch (this.conversationType.get()) {
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO:
        return getKhattabat102MessagingStore(
          this.conversationID.get()
        ).sendMessage();
      default:
        return getKhattabat101MessagingStore(
          this.conversationID.get()
        ).sendMessage();
    }
  };

  getSendButton = (): { labelKey: Ti18nKeys; color: "error" | "primary" } => {
    switch (this.conversationType.get()) {
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_C_COMMENTS:
        return { labelKey: "ADD", color: "error" };
      default:
        return { labelKey: "SEND", color: "primary" };
    }
  };

  getConversationBorder = () => {
    switch (this.conversationType.get()) {
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_C_COMMENTS:
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M_COMMENTS:
        return { border: 5, borderColor: "error.main" };
      default:
        return { border: 1, borderColor: "divider" };
    }
  };

  getConversationTitle = (): { titleKey: Ti18nKeys; titleColor: string } => {
    switch (this.conversationType.get()) {
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_C:
        return { titleKey: "ONE_O_ONE_C", titleColor: "text.primary" };
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_C_COMMENTS:
        return { titleKey: "COMMENTS", titleColor: "error.main" };
      default:
        return { titleKey: "ONE_O_TWO_WITH", titleColor: "text.primary" };
    }
  };

  getSelectionPageType = (): TSelectionPages => {
    switch (this.conversationType.get()) {
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M:
        return "matchResultPage";
      default:
        return "clientPage";
    }
  };

  getIsCommentsConversation = () => {
    switch (this.conversationType.get()) {
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_C_COMMENTS:
      case E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M_COMMENTS:
        return true;
      default:
        return false;
    }
  };

  getMessageSender = (isKhattabaMessage: boolean, senderGender: TGender) => {
    if (isKhattabaMessage) {
      return E_CHAT_SENDER.OUTGOING;
    }
    switch (senderGender) {
      case Gender.Male:
        return E_CHAT_SENDER.MALE;
      default:
        return E_CHAT_SENDER.FEMALE;
    }
  };

  appendSentMessage = ({
    message,
    messageType
  }: {
    message: string;
    messageType: TMessagesType;
  }) => {
    const databaseCurrentTime = getFormmatedDate(new Date());
    const conversationType = this.conversationType.get();
    const receiverID = getKhattabat101MessagingStore(
      this.conversationID.get()
    ).receiverID.get();
    const oneOOneMessageToAdd: TKhattabatMessage = {
      isKhattabaMessage: 1,
      isClientMessage: 0,
      message,
      messageType,
      createdOn: databaseCurrentTime,
      isSeen: 0,
      senderGender: "f",
      conversationId: this.conversationID.get()
    };
    const isComments =
      conversationType === "101C_COMMENTS" ||
      conversationType === "101M_COMMENTS";
    const isOneOOneMConversation = conversationType === "101M";

    if (isComments) {
      this.appendMessage(oneOOneMessageToAdd);
    } else {
      const messagingStore = isOneOOneMConversation
        ? getKhattabat101MMessagingStore(receiverID)
        : getKhattabat101CMessagingStore(receiverID);
      messagingStore.handleMessageSent({
        conversationID: this.conversationID.get(),
        oneOOneMessageToAdd
      });
      messagingStore.markMessagesSeen();
    }
    this.setMessageField("");
    getKhattabatFilterStore().update1o1MFilter();
  };

  appendReceivedMessage = ({
    message,
    messageType,
    senderGender,
    senderUUID
  }: {
    message: string;
    messageType: TMessagesType;
    senderGender: TGender;
    senderUUID: string;
  }) => {
    const databaseCurrentTime = getFormmatedDate(new Date());
    const conversationType = this.conversationType.get();
    const matchReceiverUUID = getKhattabat102MessagingStore(
      this.conversationID.get()
    ).matchReceiverUUID.get();
    const isOneOOneMConversation =
      conversationType === E_KHATTABAT_CONVERSATION_TYPE.ONE_O_ONE_M;
    const isOneOTwoConversation =
      conversationType === E_KHATTABAT_CONVERSATION_TYPE.ONE_O_TWO;

    const isMatchSender =
      isOneOOneMConversation ||
      (isOneOTwoConversation &&
        isDefinedString(senderUUID) &&
        senderUUID === matchReceiverUUID);

    const receivedMessageToAdd: TKhattabatMessage = {
      isKhattabaMessage: 0,
      isClientMessage: isMatchSender ? 0 : 1,
      message,
      messageType,
      createdOn: databaseCurrentTime,
      isSeen: 0,
      senderGender,
      conversationId: this.conversationID.get()
    };
    this.appendMessage(receivedMessageToAdd);
    if (conversationType === "101C") {
      getKhattabat101CGeneralStore().moveClientToTop(
        getKhattabat101MessagingStore(
          this.conversationID.get()
        ).receiverID.get()
      );
    }
    this.setUnreadMessagesCounter(this.unreadMessagesCounter.get() + 1);
    getKhattabatFilterStore().update1o1MFilter();
  };

  scrollToBottom = () => {
    if (this.scrollRef.current != null) {
      this.scrollRef.current.scrollTo({
        top: this.scrollRef.current.scrollHeight
      });
    }
  };
}

const getKhattabatSharedMessagingStore = memoize((conversationID: string) => {
  const store = new KhattabatSharedMessagingStore();
  store.conversationID.set(conversationID);
  return store;
});
export default getKhattabatSharedMessagingStore;
